import { Suspense, useDeferredValue, useState } from 'react'
import { createFileRoute, Link, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { LINK_CLASS, TraceLink } from './-components/admin/traces-shared'
import { formatCount, formatInstant } from './-components/admin/metrics-format'
import { ChartSkeleton, ErrorsPending, TableSkeleton } from './-components/admin/metrics-pending'
import {
  Chip,
  DeltaChip,
  EmptyPanel,
  ErrorRateChart,
  MetricsSection,
  SMALL_PLOT,
  SOURCE_LABELS,
  TelemetryNotice,
} from './-components/admin/metrics-panels'

// What failed over the window: the server's fault rate over time, then the errors seen most, grouped by type and
// message, each with where it happened most and its latest trace.

export const Route = createFileRoute('/_app/admin/metrics/errors')({
  component: RouteComponent,
  pendingComponent: ErrorsPending,
})

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/errors/_window' }).context
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <Suspense fallback={null}>
        <Notice />
      </Suspense>
      <MetricsSection
        copy="errors"
        actions={
          <Link to="/admin/errors" className={cn(LINK_CLASS, 'text-xs')}>
            Triage them in Errors
          </Link>
        }
      >
        <div className="flex flex-col gap-3">
          <Suspense fallback={<ChartSkeleton plot={SMALL_PLOT} />}>
            <ErrorRate />
          </Suspense>
          <Suspense fallback={<TableSkeleton rows={8} />}>
            <ErrorsTable />
          </Suspense>
        </div>
      </MetricsSection>
    </div>
  )
}

function Notice() {
  const { data } = useSuspenseQuery(useWindowContext().metricsErrorsQueryOptions)
  return <TelemetryNotice state={data.clickhouse} />
}

function ErrorRate() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsTrafficQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsTrafficQueryOptions
  return (
    <div className={cn('transition-opacity', stale && 'opacity-60')}>
      <ErrorRateChart
        data={data}
        height={SMALL_PLOT}
        description="Expected tRPC errors (UNAUTHORIZED, NOT_FOUND) are not faults."
      />
    </div>
  )
}

// A laptop screen's worth under the chart; the rest is one click away.
const PAGE_ROWS = 10

function ErrorsTable() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsErrorsQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsErrorsQueryOptions
  const [all, setAll] = useState(false)
  if (data.errors.length === 0)
    return <EmptyPanel>{data.clickhouse === 'ok' ? 'No error in this window.' : 'No errors to rank.'}</EmptyPanel>
  const shown = all ? data.errors : data.errors.slice(0, PAGE_ROWS)
  return (
    <div className={cn('flex flex-col gap-3 transition-opacity', stale && 'opacity-60')}>
      <div className="scrollbar w-full overflow-x-auto rounded-xl border">
        <Table className="min-w-[860px] table-fixed border-collapse">
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[44%] px-3 py-2">Error</TableHead>
              <TableHead className="text-muted-foreground px-3 py-2">Where</TableHead>
              <TableHead className="text-muted-foreground w-24 px-3 py-2 text-right">Events</TableHead>
              <TableHead className="text-muted-foreground w-24 px-3 py-2 text-right">Sessions</TableHead>
              <TableHead className="text-muted-foreground w-32 px-3 py-2 text-right">Last seen</TableHead>
              <TableHead className="text-muted-foreground w-20 px-3 py-2 text-right">Trace</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row) => (
              <TableRow key={`${row.source}:${row.type}:${row.message}`}>
                <TableCell className="px-3 py-2">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <Chip>{SOURCE_LABELS[row.source]}</Chip>
                      <span className="truncate text-xs font-medium">{row.type}</span>
                    </span>
                    {row.message && (
                      <span className="text-muted-foreground truncate text-xs" title={row.message}>
                        {row.message}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="truncate px-3 py-2 font-mono text-xs" title={row.where ?? undefined}>
                  {row.where ?? '—'}
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                  <div className="flex flex-col items-end">
                    <span className="font-medium">{formatCount(row.count)}</span>
                    <DeltaChip deltaPct={row.countDeltaPct} better="down" className="text-[10px]" />
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">{formatCount(row.sessions)}</TableCell>
                <TableCell className="text-muted-foreground px-3 py-2 text-right text-xs tabular-nums">
                  {formatInstant(Date.parse(row.lastSeen), 60_000)}
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs">
                  {row.sampleTraceId ? (
                    <TraceLink traceId={row.sampleTraceId} at={row.lastSeen} className={LINK_CLASS}>
                      latest
                    </TraceLink>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.errors.length > PAGE_ROWS && (
        <Button variant="outline" size="sm" className="self-start" onClick={() => setAll((prev) => !prev)}>
          {all ? `Show the top ${PAGE_ROWS}` : `Show all ${data.errors.length}`}
        </Button>
      )}
    </div>
  )
}
